import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, ChevronLeft, ChevronRight, CloudOff, FileText, Flag, MoreHorizontal, Target, Trash2 } from 'lucide-react';
import {
  allItems, computeScore, describeGoal, evaluateGoals, evaluateTemperature, type GoalLike, type LaborInputs, type TemplateItem,
} from '@bp/shared';
import { api, ApiError } from '@/lib/api';
import { onJobFlushed, pendingFor, sendOrQueue, flush, useOutbox } from '@/lib/outbox';
import type { AuditDetail, MediaRow, ResponseRow } from '@/lib/types';
import { cn, fmtDate } from '@/lib/utils';
import { ModeBadge } from '../badges';
import { Badge, Button, Card, CardHeader, Dialog, ErrorNote, Textarea } from '../ui';
import { ItemCard, type ItemPatch } from './ItemCard';
import { LaborPanel } from './LaborPanel';
import { CaptureButtons, MediaThumbs, type Thumb } from './Media';

const LABOR = '__labor';
const SUMMARY = '__summary';
type LocalThumb = Thumb & { item_key: string };

function applyPatch(r: ResponseRow, item: TemplateItem, p: ItemPatch): ResponseRow {
  const next = { ...r };
  if (p.comment !== undefined) next.comment = p.comment;
  if (p.temperature !== undefined) {
    next.temperature = p.temperature;
    next.result = p.temperature === null ? 'pending' : evaluateTemperature(p.temperature, item);
  }
  if (p.result !== undefined) {
    next.result = p.result;
    if (p.result === 'na') next.temperature = null;
  }
  if (next.result !== 'fail') next.fix_by_window = null;
  else if (p.fix_by_window !== undefined) next.fix_by_window = p.fix_by_window;
  return next;
}

export function LiveAudit({ data, refetch }: { data: AuditDetail; refetch: () => Promise<unknown> }) {
  const a = data.audit;
  const content = data.template.content;
  const qc = useQueryClient();
  const nav = useNavigate();
  const outbox = useOutbox();
  const items = useMemo(() => allItems(content), [content]);
  const itemByKey = useMemo(() => new Map(items.map((i) => [i.key, i])), [items]);

  const [responses, setResponses] = useState<Record<string, ResponseRow>>(() => Object.fromEntries(data.responses.map((r) => [r.item_key, r])));
  const [serverMedia, setServerMedia] = useState<MediaRow[]>(data.media);
  const [localMedia, setLocalMedia] = useState<LocalThumb[]>([]);
  const [tab, setTab] = useState<string>(content.sections[0]?.key ?? SUMMARY);
  const [notes, setNotes] = useState(a.notes);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const chains = useRef(new Map<string, Promise<unknown>>());
  const topRef = useRef<HTMLDivElement>(null);

  // When fresh server data arrives, take it — except for items with changes still queued here.
  useEffect(() => {
    const queued = new Set(pendingFor(a.id).filter((j) => j.kind === 'response').map((j) => j.itemKey));
    setResponses((cur) => Object.fromEntries(data.responses.map((r) => [r.item_key, queued.has(r.item_key) ? cur[r.item_key] ?? r : r])));
    setServerMedia(data.media);
  }, [data, a.id]);

  useEffect(() => onJobFlushed((job) => {
    if (job.auditId !== a.id) return;
    if (job.kind === 'upload') {
      // A queued photo reached the server: drop the oldest local placeholder for that item.
      setLocalMedia((l) => {
        const i = l.findIndex((m) => m.item_key === job.itemKey && m.pending);
        return i < 0 ? l : l.filter((_, j) => j !== i);
      });
    }
    void refetch();
  }), [a.id, refetch]);

  const summary = useMemo(() => computeScore(content, Object.values(responses)), [content, responses]);
  const goals: GoalLike[] = data.live_goal_results.map((g) => ({ id: g.goal_id, metric: g.metric, section_key: g.section_key, target: g.target, label: g.label }));
  const liveGoals = evaluateGoals(goals, summary);
  const sectionLabel = (k: string | null) => content.sections.find((s) => s.key === k)?.label;

  const onChange = useCallback((itemKey: string, patch: ItemPatch) => {
    const item = itemByKey.get(itemKey)!;
    setResponses((cur) => ({ ...cur, [itemKey]: applyPatch(cur[itemKey], item, patch) }));
    // One request at a time per item, in order.
    const prev = chains.current.get(itemKey) ?? Promise.resolve();
    const next = prev.then(async () => {
      try {
        const res = await sendOrQueue<{ response: ResponseRow; stale: boolean }>({ kind: 'response', auditId: a.id, itemKey, body: patch });
        if (res?.stale) {
          toast.warning('Someone else changed this item more recently; showing their answer.');
          setResponses((cur) => ({ ...cur, [itemKey]: res.response }));
        } else if (res) {
          setResponses((cur) => ({ ...cur, [itemKey]: { ...cur[itemKey], answered_at: res.response.answered_at } }));
        }
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Could not save.');
        void refetch();
      }
    });
    chains.current.set(itemKey, next);
  }, [a.id, itemByKey, refetch]);

  const onFiles = useCallback(async (itemKey: string, files: File[]) => {
    for (const file of files) {
      const localUrl = URL.createObjectURL(file);
      const kind = file.type.startsWith('video/') ? 'video' : 'photo';
      const tempId = `local-${crypto.randomUUID()}`;
      setLocalMedia((l) => [...l, { id: tempId, item_key: itemKey, kind, content_type: file.type, localUrl, pending: true }]);
      try {
        const res = await sendOrQueue<MediaRow>({ kind: 'upload', auditId: a.id, itemKey, blob: file, filename: file.name || `${kind}.${file.type.split('/')[1] ?? 'bin'}` });
        if (res) {
          setServerMedia((m) => [...m, res]);
          setLocalMedia((l) => l.filter((x) => x.id !== tempId));
          URL.revokeObjectURL(localUrl);
        }
      } catch (e) {
        setLocalMedia((l) => l.filter((x) => x.id !== tempId));
        toast.error(e instanceof Error ? e.message : 'Upload failed.');
      }
    }
  }, [a.id]);

  const onDeleteMedia = useCallback(async (id: string) => {
    if (!confirm('Remove this attachment?')) return;
    try {
      await api.del(`/api/media/${id}`);
      setServerMedia((m) => m.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not remove.');
    }
  }, []);

  const thumbsFor = (key: string): Thumb[] => [
    ...serverMedia.filter((m) => m.item_key === key),
    ...localMedia.filter((m) => m.item_key === key),
  ];

  const notesTimer = useRef<number | undefined>(undefined);
  const saveAudit = useCallback((body: { notes?: string; labor_inputs?: LaborInputs }) => {
    api.patch(`/api/audits/${a.id}`, body).catch((e: unknown) => toast.error(e instanceof ApiError && e.status === 0 ? 'Offline — reconnect to save notes and labor figures.' : (e as Error).message));
  }, [a.id]);

  const tabs = [
    ...content.sections.map((s) => {
      const rs = s.items.map((i) => responses[i.key]);
      return { key: s.key, label: s.label, done: rs.filter((r) => r && r.result !== 'pending').length, total: s.items.length, fails: rs.filter((r) => r?.result === 'fail').length };
    }),
    ...(content.labor_config.enabled ? [{ key: LABOR, label: 'Labor', done: 0, total: 0, fails: 0 }] : []),
    { key: SUMMARY, label: 'Review & finish', done: 0, total: 0, fails: 0 },
  ];
  const tabIndex = tabs.findIndex((t) => t.key === tab);
  const go = (key: string) => {
    setTab(key);
    topRef.current?.scrollIntoView({ block: 'start' });
  };
  const pendingItems = items.filter((i) => responses[i.key]?.result === 'pending');
  const failsWithoutFixBy = items.filter((i) => responses[i.key]?.result === 'fail' && !responses[i.key]?.fix_by_window);
  const progress = summary.total_items ? Math.round((summary.answered / summary.total_items) * 100) : 0;
  const section = content.sections.find((s) => s.key === tab);
  let counter = 0;
  const numbering = new Map(items.map((i) => [i.key, ++counter]));

  return (
    <div ref={topRef} className="-mt-5 sm:-mt-6">
      {/* Sticky header: where you are, running score, progress */}
      <div className="sticky top-0 z-20 -mx-4 border-b bg-bg/95 px-4 pt-4 pb-3 backdrop-blur sm:-mx-6 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <Link to={`/stores/${a.store_id}`} className="inline-flex items-center gap-1 text-sm text-muted-fg hover:text-fg"><ChevronLeft className="size-4" />{data.store.name}</Link>
            <div className="mt-0.5 flex flex-wrap items-center gap-2">
              <h1 className="text-lg font-semibold">{data.template.name}</h1>
              <ModeBadge mode={a.mode} />
              <span className="text-xs text-muted-fg">v{data.template.version_no} · {fmtDate(a.audit_date)}</span>
              {!outbox.online && <Badge tone="warn"><CloudOff className="size-3" />Offline — saving on this device</Badge>}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="tabular text-2xl leading-none font-bold">{summary.score}%</p>
              <p className="text-xs text-muted-fg">{summary.answered}/{summary.total_items} answered</p>
            </div>
            <Button size="lg" onClick={() => setCompleteOpen(true)}><CheckCircle2 className="size-5" />Finish</Button>
            <div className="relative">
              <Button variant="ghost" size="lg" className="px-2" onClick={() => setMenuOpen((o) => !o)} aria-label="More"><MoreHorizontal className="size-5" /></Button>
              {menuOpen && (
                <div className="absolute right-0 z-30 mt-1 w-52 rounded-lg border bg-card py-1 shadow-lg" onMouseLeave={() => setMenuOpen(false)}>
                  <Link to={`/audits/${a.id}/print`} target="_blank" className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted"><FileText className="size-4" />Print draft</Link>
                  {data.can.delete && (
                    <button
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-fail hover:bg-muted"
                      onClick={async () => {
                        if (!confirm('Discard this audit and everything entered? This cannot be undone.')) return;
                        try {
                          await api.del(`/api/audits/${a.id}`);
                          await qc.invalidateQueries({ queryKey: ['audits'] });
                          nav(`/stores/${a.store_id}`);
                        } catch (e) { toast.error((e as Error).message); }
                      }}
                    ><Trash2 className="size-4" />Discard audit</button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-muted"><div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} /></div>
        {liveGoals.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {liveGoals.map((g) => (
              <Badge key={g.goal_id} tone={g.met ? 'pass' : 'warn'}><Target className="size-3" />{describeGoal(g, sectionLabel(g.section_key) ?? undefined)}: {g.actual}{g.metric === 'max_failed_items' ? '' : '%'}</Badge>
            ))}
          </div>
        )}
        <div className="-mx-1 mt-3 flex gap-1.5 overflow-x-auto px-1 pb-1 [scrollbar-width:none]">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => go(t.key)}
              className={cn('flex flex-none items-center gap-2 rounded-full border px-3.5 py-2 text-sm font-medium whitespace-nowrap',
                tab === t.key ? 'border-primary bg-primary text-primary-fg' : 'bg-card hover:bg-muted')}
            >
              {t.label}
              {t.total > 0 && <span className={cn('tabular text-xs', tab === t.key ? 'text-primary-fg/80' : 'text-muted-fg')}>{t.done}/{t.total}</span>}
              {t.fails > 0 && <span className="tabular rounded-full bg-fail px-1.5 text-xs text-white">{t.fails}</span>}
              {t.total > 0 && t.done === t.total && t.fails === 0 && <CheckCircle2 className="size-4 text-pass" />}
            </button>
          ))}
        </div>
      </div>

      <div className="pt-5">
        {section && (
          <div className="space-y-3">
            {section.items.map((item) => (
              <ItemCard
                key={item.key}
                index={numbering.get(item.key)!}
                item={item}
                response={responses[item.key]}
                previous={data.previous?.items[item.key]}
                media={thumbsFor(item.key)}
                onChange={(p) => onChange(item.key, p)}
                onFiles={(f) => onFiles(item.key, f)}
                onDeleteMedia={onDeleteMedia}
              />
            ))}
          </div>
        )}

        {tab === LABOR && (
          <LaborPanel inputs={a.labor_inputs} onChange={(labor_inputs) => saveAudit({ labor_inputs })}>
            <Card>
              <CardHeader title="Labor notes & media" />
              <div className="space-y-3 p-4">
                <CaptureButtons photo={content.labor_config.allow_photo} video={content.labor_config.allow_video} onFiles={(f) => onFiles(LABOR, f)} />
                <MediaThumbs items={thumbsFor(LABOR)} onDelete={onDeleteMedia} />
              </div>
            </Card>
          </LaborPanel>
        )}

        {tab === SUMMARY && (
          <div className="grid gap-5 lg:grid-cols-2">
            <Card>
              <CardHeader title="Where things stand" />
              <div className="space-y-2 p-4 text-sm">
                {content.sections.map((s) => (
                  <div key={s.key} className="flex items-center gap-3">
                    <button className="w-44 truncate text-left hover:underline" onClick={() => go(s.key)}>{s.label}</button>
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted"><div className={cn('h-full', summary.section_scores[s.key] >= content.scoring_rules.passing_threshold ? 'bg-pass' : summary.section_scores[s.key] >= content.scoring_rules.warning_threshold ? 'bg-warn' : 'bg-fail')} style={{ width: `${summary.section_scores[s.key]}%` }} /></div>
                    <span className="tabular w-10 text-right font-medium">{summary.section_scores[s.key]}%</span>
                  </div>
                ))}
              </div>
              {pendingItems.length > 0 && (
                <div className="border-t px-4 py-3 text-sm">
                  <p className="font-medium text-warn">{pendingItems.length} item{pendingItems.length === 1 ? '' : 's'} not answered</p>
                  <button className="mt-1 text-info hover:underline" onClick={() => { const first = pendingItems[0]; go(first.section_key); setTimeout(() => document.getElementById(`item-${first.key}`)?.scrollIntoView({ block: 'center' }), 50); }}>Jump to the first one</button>
                </div>
              )}
            </Card>
            <Card>
              <CardHeader title="Audit notes" subtitle="Overall observations. Saved as you type." />
              <div className="p-4">
                <Textarea className="min-h-40" value={notes} onChange={(e) => { setNotes(e.target.value); window.clearTimeout(notesTimer.current); const v = e.target.value; notesTimer.current = window.setTimeout(() => saveAudit({ notes: v }), 800); }} />
              </div>
            </Card>
          </div>
        )}

        <div className="mt-6 flex justify-between gap-3">
          <Button variant="outline" size="lg" disabled={tabIndex <= 0} onClick={() => go(tabs[tabIndex - 1].key)}><ChevronLeft className="size-5" />Back</Button>
          {tabIndex < tabs.length - 1
            ? <Button variant="outline" size="lg" onClick={() => go(tabs[tabIndex + 1].key)}>Next: {tabs[tabIndex + 1].label}<ChevronRight className="size-5" /></Button>
            : <Button size="lg" onClick={() => setCompleteOpen(true)}><CheckCircle2 className="size-5" />Finish audit</Button>}
        </div>
      </div>

      <CompleteDialog
        open={completeOpen}
        onOpenChange={setCompleteOpen}
        data={data}
        summary={summary}
        pending={pendingItems.length}
        failsWithoutFixBy={failsWithoutFixBy.length}
        onDone={async () => {
          await qc.invalidateQueries({ queryKey: ['audits'] });
          await qc.invalidateQueries({ queryKey: ['store', a.store_id] });
          await refetch();
        }}
      />
    </div>
  );
}

function CompleteDialog({ open, onOpenChange, data, summary, pending, failsWithoutFixBy, onDone }: {
  open: boolean; onOpenChange: (o: boolean) => void; data: AuditDetail; summary: ReturnType<typeof computeScore>; pending: number; failsWithoutFixBy: number; onDone: () => Promise<void>;
}) {
  const a = data.audit;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const official = a.mode === 'official';
  const blocked = official && pending > 0;

  async function complete() {
    setBusy(true);
    setError(null);
    try {
      await flush();
      if (pendingFor(a.id).length) throw new Error('Some answers are still saved only on this device. Reconnect to the internet, then finish.');
      const r = await api.post<{ action_items_created: number }>(`/api/audits/${a.id}/complete`);
      toast.success(official ? `Audit locked. ${r.action_items_created} action item${r.action_items_created === 1 ? '' : 's'} created.` : 'Practice audit finished.');
      onOpenChange(false);
      await onDone();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={official ? 'Finish and lock this audit?' : 'Finish this practice audit?'}
      footer={<><Button variant="outline" onClick={() => onOpenChange(false)}>Keep working</Button><Button onClick={complete} loading={busy} disabled={blocked}>{official ? 'Finish & lock' : 'Finish'}</Button></>}
    >
      <div className="space-y-3 text-sm">
        <div className="flex items-center gap-4 rounded-lg bg-muted px-4 py-3">
          <p className="tabular text-3xl font-bold">{summary.score}%</p>
          <p className="text-muted-fg">{summary.passed} pass · {summary.failed} fail · {summary.na} N/A{pending > 0 && ` · ${pending} unanswered`}</p>
        </div>
        {blocked && <p className="flex items-start gap-2 text-fail"><AlertTriangle className="mt-0.5 size-4 flex-none" />Official audits need every item answered. {pending} left.</p>}
        {!official && pending > 0 && <p className="text-warn">{pending} unanswered items won't count toward the practice score.</p>}
        {official && failsWithoutFixBy > 0 && <p className="flex items-start gap-2 text-warn"><Flag className="mt-0.5 size-4 flex-none" />{failsWithoutFixBy} failed item{failsWithoutFixBy === 1 ? ' has' : 's have'} no fix-by window, so {failsWithoutFixBy === 1 ? 'its' : 'their'} task will have no due date.</p>}
        {official
          ? <p className="text-muted-fg">Finishing updates {data.store.name}'s score, locks the answers, and creates an action item for each failed item. Only a regional manager or admin can unlock it.</p>
          : <p className="text-muted-fg">Practice results are kept for comparison and never change the store's official score.</p>}
        <ErrorNote error={error} />
      </div>
    </Dialog>
  );
}
